/* eslint-disable max-len */
import { ChevronDown } from 'lucide-react';
import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';

import type { ContextRegistry, ContextRegistryCategory } from '@/services/api/admin';

import { ShareStrip } from '@/components/admin/StatTile';
import { isHiddenSubscore } from '@/components/dashboard/scoring';
import { Button, Segmented, TextInput } from '@/components/ds';
import { Bars, Donut } from '@/components/viz';
import { useAuthContext } from '@/context/AuthProvider';

// ─── Design tokens (shared with UsersTab / PropertiesTab) ───────────────────
const C = {
  bg: 'var(--tx-fill-2)',
  card: 'var(--tx-glass-panel)',
  input: 'var(--tx-fill)',
  border: 'var(--tx-line)',
  borderLight: 'var(--tx-line)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
  lime: '#c0ff02',
  danger: '#ff4d4d',
};
const L = {
  critical: '#DB5282',
  urgent: '#FF9A4D',
  moderate: '#FFF981',
  low: '#5AE9FF',
  monitor: '#9A938A',
  resolved: '#C0FF02',
};
const font = "'Manrope', sans-serif";

// The app's legend tick: a 2px x 12px hairline in the data colour, used in place of tinted pills and dots
function Tick({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      style={{ width: 2, height: 12, borderRadius: 1, background: color, flexShrink: 0, display: 'inline-block' }}
    />
  );
}

// ─── Types ──────────────────────────────────────────────────────────────────
interface SubscoreEntry {
  label: string;
  weight: number;
  question: string;
}

interface CategoryEntry {
  weight: number;
  subscore: string;
}

interface GradeBand {
  min: number;
  color: string;
  label: string;
}

interface AuditEntry {
  ts: string;
  user: string;
  field: string;
  old: any;
  new: any;
  scope: string;
}

interface CmsState {
  version: string;
  lastSaved: string | null;
  dirty: boolean;
  condSubscores: Record<string, SubscoreEntry>;
  ctxSubscores: Record<string, SubscoreEntry>;
  condCategories: Record<string, CategoryEntry>;
  ctxCategories: Record<string, CategoryEntry>;
  urgencyPoints: Record<string, number>;
  bigTicketMultiplier: number;
  normalization: {
    floor: number;
    ceiling: number;
  };
  urgencyCostMult: Record<string, number>;
  repairBurden: { curveParam: number; note: string };
  gradeBands: Record<string, GradeBand>;
  gradeToScore: Record<string, number>;
  ratingToScore: Record<string, number>;
  content: {
    askHauserPrompt: string;
    propertySummaryTemplate: string;
    gradeInterpretations: {
      conditionScore: Record<string, string>;
      contextScore: Record<string, string>;
    };
    assessmentSummary: { conditionScore: string; contextScore: string };
    conditionScoreInsights: string;
    contextScoreInsights: string;
    conditionScoreNotes: string;
    contextScoreNotes: string;
    documentClassificationPrompt: string;
    issueExtractionPrompt: string;
    conditionExplainerPrompt: string;
    contextExplainerPrompt: string;
  };
  auditLog: AuditEntry[];
}

// Deprecated top-level fields from the pre-cleanup (Livability) config shape.
// A production-saved config predating that cleanup can still carry these —
// getScoringConfig() is untyped (Record<string, any>), so nothing stops a
// stale key from surviving the {...INITIAL, ...savedConfig} merge otherwise.
const DEPRECATED_TOP_LEVEL_KEYS = [
  'livabilityDeductions',
  'livabilityMultiplier',
  'livabilityUrgencyMod',
  'livabilityDurationFactor',
  'livabilityTimePenaltyMultiplier',
  'livabilityDCap',
];

const REQUIRED_PROMPT_FIELDS = {
  askHauserPrompt: 'Ask Hauser Prompt',
  documentClassificationPrompt: 'Document Classification Prompt',
  issueExtractionPrompt: 'Issue Extraction Prompt',
  conditionExplainerPrompt: 'The Home Explainer',
  contextExplainerPrompt: 'The Area Explainer',
} as const;

const CONTEXT_CATEGORY_KEYS = [
  'LOCATION',
  'AMENITIES',
  'SITE',
  'NATURAL_HAZARDS',
  'HEALTH_QUALITY',
  'SAFETY',
] as const;

// Strips deprecated Livability/InvestmentSignal keys from a persisted scoring
// config before merging it over INITIAL, so a config saved before that
// cleanup doesn't reintroduce them into the composite formula, subscore
// tabs, or category weight groups.
const sanitizeDeprecatedConfig = (raw: Record<string, any>): Record<string, any> => {
  const cleaned = { ...raw };
  for (const key of DEPRECATED_TOP_LEVEL_KEYS) delete cleaned[key];

  if (cleaned.condSubscores) {
    const { livability: _livability, ...condSubscores } = cleaned.condSubscores;
    cleaned.condSubscores = condSubscores;
  }
  if (cleaned.ctxSubscores) {
    const { investmentSignal: _investmentSignal, ...ctxSubscores } = cleaned.ctxSubscores;
    cleaned.ctxSubscores = ctxSubscores;
  }
  if (cleaned.ctxCategories) {
    const {
      HISTORY: _history,
      MARKET: _market,
      HAZARDS: _hazards,
      NEIGHBORHOOD: _neighborhood,
      ...ctxCategories
    } = cleaned.ctxCategories;
    const hasCompleteSixPillarSchema =
      Object.keys(ctxCategories).length === CONTEXT_CATEGORY_KEYS.length &&
      CONTEXT_CATEGORY_KEYS.every((key) => key in ctxCategories);

    // Category weights are one atomic schema. Do not let a persisted four-pillar
    // config partially replace the six-pillar defaults during rollout.
    if (hasCompleteSixPillarSchema) cleaned.ctxCategories = ctxCategories;
    else delete cleaned.ctxCategories;
  }

  return cleaned;
};

// ─── Initial state ──────────────────────────────────────────────────────────
const INITIAL: CmsState = {
  version: '1.3.1',
  lastSaved: null,
  dirty: false,
  condSubscores: {
    homeHealth: {
      label: 'HomeHealth',
      weight: 100,
      question: 'What kind of shape is this house actually in?',
    },
    repairBurden: {
      label: 'RepairBurden',
      weight: 0,
      question: 'How much am I going to have to spend fixing this place?',
    },
  },
  ctxSubscores: {
    locationQuality: {
      label: 'LocationQuality',
      weight: 60,
      question: 'Is this a good area to live in?',
    },
    riskExposure: {
      label: 'RiskExposure',
      weight: 40,
      question: 'How safe is this property and the area around it?',
    },
  },
  condCategories: {
    STRUCTURAL: { weight: 25, subscore: 'homeHealth' },
    EXTERIOR: { weight: 20, subscore: 'homeHealth' },
    HVAC_SYSTEMS: { weight: 15, subscore: 'homeHealth' },
    PLUMBING: { weight: 15, subscore: 'homeHealth' },
    ELECTRICAL: { weight: 15, subscore: 'homeHealth' },
    INTERIOR: { weight: 10, subscore: 'homeHealth' },
  },
  ctxCategories: {
    LOCATION: { weight: 40, subscore: 'locationQuality' },
    AMENITIES: { weight: 35, subscore: 'locationQuality' },
    SITE: { weight: 25, subscore: 'locationQuality' },
    NATURAL_HAZARDS: { weight: 25, subscore: 'riskExposure' },
    HEALTH_QUALITY: { weight: 45, subscore: 'riskExposure' },
    SAFETY: { weight: 30, subscore: 'riskExposure' },
  },
  urgencyPoints: {
    Critical: 5,
    Urgent: 4,
    Moderate: 3,
    Low: 2,
    Monitor: 1,
  },
  bigTicketMultiplier: 1.5,
  normalization: {
    floor: 5,
    ceiling: 95,
  },
  urgencyCostMult: {
    Critical: 3.0,
    Urgent: 2.5,
    Moderate: 1.5,
    Low: 1.0,
    Monitor: 0.5,
  },
  repairBurden: {
    curveParam: 10,
    note: 'Controls how fast score decays vs. repair%. Lower = steeper decay.',
  },
  gradeBands: {
    A: { min: 86, color: '#f3f3f3', label: 'Excellent — minimal concerns' },
    B: { min: 77, color: '#5AE9FF', label: 'Good — minor issues' },
    C: { min: 68, color: '#FFF981', label: 'Fair — moderate concerns' },
    D: { min: 59, color: '#FF9A4D', label: 'Below Average — significant issues' },
    F: { min: 0, color: '#DB5282', label: 'Poor — major concerns' },
  },
  gradeToScore: { A: 93, B: 83, C: 73, D: 63, F: 53, null: 68 },
  ratingToScore: { 'Very Low': 93, Low: 83, Moderate: 73, High: 63, 'Very High': 53 },
  content: {
    askHauserPrompt: '',
    propertySummaryTemplate:
      'This property earned a ConditionScore of {{conditionScore}} ({{condGrade}}) and a ContextScore of {{contextScore}} ({{ctxGrade}}). {{condSummary}} {{ctxSummary}} {{repairBurdenSentence}}',
    gradeInterpretations: {
      conditionScore: {
        A: 'This property is in excellent physical condition with minimal issues detected across all major systems.',
        B: 'This property is in good condition with minor issues that are manageable and non-urgent.',
        C: 'This property shows moderate concerns worth addressing — budget for repairs before or shortly after purchase.',
        D: 'This property has significant issues across one or more systems. Negotiate repair credits and verify scope carefully.',
        F: 'This property has major deficiencies that may affect habitability or safety. Proceed with extreme caution.',
      },
      contextScore: {
        A: 'This location offers excellent amenities and low risk exposure.',
        B: 'This is a good location with minor risk factors and solid neighborhood fundamentals.',
        C: 'This location has mixed signals — some strong attributes alongside moderate risk factors worth monitoring.',
        D: 'This location presents notable risk factors or amenity deficiencies that should factor into your decision.',
        F: 'This location has serious risk or quality concerns. Evaluate carefully against your priorities.',
      },
    },
    assessmentSummary: {
      conditionScore:
        'The ConditionScore evaluates the physical state of the property — structural integrity, major systems, and interior quality. It aggregates inspection findings weighted by severity and urgency into a single 5–95 score.',
      contextScore:
        'The ContextScore assesses the area across natural hazards, health and environmental quality, safety, location access, amenities, and parcel characteristics. It remains independent of the physical condition of the home.',
    },
    conditionScoreInsights:
      'The ConditionScore reflects the physical health of the property across structural, mechanical, and interior systems. It synthesizes inspection findings, urgency classifications, and estimated repair costs into a single 5–95 score. Higher scores indicate fewer and less severe issues.',
    contextScoreInsights:
      'The ContextScore evaluates the area across six pillars: Hazards, Health, Safety, Access, Amenities, and Parcel. It combines location quality with risk exposure without evaluating the physical condition of the home.',
    conditionScoreNotes: '',
    contextScoreNotes: '',
    documentClassificationPrompt: '',
    issueExtractionPrompt: '',
    conditionExplainerPrompt: '',
    contextExplainerPrompt: '',
  },
  auditLog: [
    {
      ts: '2026-03-10 14:22',
      user: 'James Dunn',
      field: 'gradeBands.A.min',
      old: 85,
      new: 86,
      scope: 'Grade Bands',
    },
    {
      ts: '2026-02-28 09:41',
      user: 'James Dunn',
      field: 'urgencyPoints.Critical',
      old: 4,
      new: 5,
      scope: 'Urgency Points',
    },
  ],
};

// ─── NAV items ──────────────────────────────────────────────────────────────
const NAV = [
  { key: 'weights', label: 'Score weights' },
  { key: 'grading', label: 'Grading' },
  { key: 'content', label: 'LLM Content' },
  { key: 'audit', label: 'Audit log' },
];

// ─── Donut helpers ──────────────────────────────────────────────────────────

const SEGMENT_PALETTE = [
  L.critical,
  L.urgent,
  L.moderate,
  L.low,
  C.muted,
  L.resolved,
  L.monitor,
  '#A78BFA',
  '#34D399',
];

// The category mix as the app's donut (components/viz): slices in each category's colour, keyed so a zero-weight
// category never shifts the others' colours; the sum and its balance in the middle
// category keys arrive as SHOUTED_SNAKE ("HVAC_SYSTEMS"); shown in sentence case, acronyms kept
const ACRONYMS = new Set(['HVAC', 'HOA', 'ADU', 'NHD']);
const sentenceCase = (key: string) =>
  key
    .split(/[_\s]+/)
    .map((w, i) => {
      const up = w.toUpperCase();
      if (ACRONYMS.has(up)) return up;
      const low = w.toLowerCase();
      return i === 0 ? low.charAt(0).toUpperCase() + low.slice(1) : low;
    })
    .join(' ');

function CategoryDonut({
  cats,
  colors,
  scale = 1,
}: {
  cats: [string, { weight: number }][];
  colors: string[];
  scale?: number;
}) {
  const total = cats.reduce((s, [, v]) => s + v.weight, 0);
  const ok = Math.abs(total - 100) < 0.01;
  const size = Math.round(220 * scale);
  return (
    <div style={{ width: size, display: 'flex', justifyContent: 'center' }}>
      <Donut
        size={size}
        parts={cats.map(([key, v], i) => ({
          key,
          label: key.replace(/_/g, ' '),
          value: v.weight,
          color: colors[i] || C.muted,
        }))}
        format={(v) => `${v}%`}
        label={`${total}%`}
        sub={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Tick color={ok ? L.resolved : L.urgent} />
            {ok ? 'balanced' : 'must equal 100%'}
          </span>
        }
      />
    </div>
  );
}

// ─── Shared UI components ───────────────────────────────────────────────────

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        background: C.card,
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
        borderRadius: '24px',
        padding: 20,
        ...style,
      }}>
      {children}
    </div>
  );
}

function SectionHead({ title, sub, badge }: { title: string; sub?: string; badge?: string }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <h2
          style={{
            margin: 0,
            fontSize: 17,
            fontWeight: 400,
            letterSpacing: '-0.015em',
            color: C.text,
            fontFamily: font,
          }}>
          {title}
        </h2>
        {badge && <TierBadge label={badge} color={L.resolved} />}
      </div>
      {sub && (
        <p style={{ margin: 0, fontSize: 11, fontWeight: 500, color: C.muted, fontFamily: font }}>
          {sub}
        </p>
      )}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        margin: '0 0 6px',
        fontSize: 11,
        fontWeight: 600,
        color: C.muted,
        fontFamily: font,
      }}>
      {children}
    </p>
  );
}

function NumInput({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  unit = '',
  warn = false,
  fullWidth = false,
  style = {},
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  warn?: boolean;
  fullWidth?: boolean;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        ...(fullWidth ? { width: '100%' } : {}),
        ...style,
      }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          background: 'var(--tx-fill)',
          border: warn ? `1px solid ${L.urgent}` : 0,
          borderRadius: 10,
          overflow: 'hidden',
          ...(fullWidth ? { flex: 1 } : {}),
        }}>
        <input
          type="text"
          value={value}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
          }}
          style={{
            background: 'transparent',
            border: 'none',
            color: warn ? L.urgent : 'var(--tx-muted)',
            padding: '7px 10px',
            fontSize: 14,
            fontWeight: 600,
            outline: 'none',
            width: fullWidth ? '100%' : 56,
            minWidth: 0,
            textAlign: 'center',
            fontFamily: font,
          }}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <button
            onClick={() => onChange(Math.min(max, value + step))}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: C.muted,
              padding: '2px 6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: `1px solid ${C.border}`,
            }}>
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
              <path d="M4 0.5L7.5 4.5H0.5L4 0.5Z" fill="currentColor" />
            </svg>
          </button>
          <button
            onClick={() => onChange(Math.max(min, value - step))}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: C.muted,
              padding: '2px 6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
              <path d="M4 4.5L0.5 0.5H7.5L4 4.5Z" fill="currentColor" />
            </svg>
          </button>
        </div>
      </div>
      {unit && (
        <span style={{ fontSize: 12, color: C.muted, width: 20, textAlign: 'left' }}>{unit}</span>
      )}
    </div>
  );
}

function _TextInput({
  value,
  onChange,
  mono,
}: {
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        background: 'var(--tx-fill)',
        border: 0,
        borderRadius: 8,
        color: C.text,
        padding: '8px 12px',
        fontSize: mono ? 12 : 13,
        outline: 'none',
        width: '100%',
        fontFamily: mono ? 'monospace' : font,
        boxSizing: 'border-box' as const,
      }}
    />
  );
}

function Textarea({
  value,
  onChange,
  rows = 5,
  mono,
  autoSize = true,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  mono?: boolean;
  autoSize?: boolean;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resizeToFit = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    if (autoSize) resizeToFit();
  }, [value, autoSize, resizeToFit]);

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      style={{
        background: 'var(--tx-fill)',
        border: 0,
        borderRadius: 8,
        color: C.text,
        padding: '10px 12px',
        fontSize: mono ? 12 : 13,
        outline: 'none',
        width: '100%',
        fontFamily: mono ? 'monospace' : font,
        resize: 'vertical' as const,
        lineHeight: 1.6,
        boxSizing: 'border-box' as const,
        height: autoSize ? undefined : '100%',
        minHeight: autoSize ? undefined : 0,
        overflow: autoSize ? 'hidden' : 'auto',
      }}
    />
  );
}

function WeightSum({ weights }: { weights: Record<string, number | { weight: number }> }) {
  const sum = Object.values(weights).reduce(
    (s: number, v) => s + (typeof v === 'object' ? v.weight : v),
    0,
  );
  const ok = Math.abs(sum - 100) < 0.01;
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 500,
        color: C.muted,
        fontFamily: font,
        whiteSpace: 'nowrap',
      }}>
      <Tick color={ok ? L.resolved : L.urgent} />
      <span style={{ color: C.text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {sum.toFixed(1)}%
      </span>
      <span>{ok ? 'balanced' : 'must equal 100%'}</span>
    </div>
  );
}

function TierBadge({ label, color }: { label: string; color: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        fontWeight: 500,
        color: C.muted,
        whiteSpace: 'nowrap',
        fontFamily: font,
        lineHeight: 1,
      }}>
      <Tick color={color} />
      {label}
    </span>
  );
}

// ─── ColorPicker (frosted glass popover) ─────────────────────────────────────

function hsvToHex(h: number, s: number, v: number) {
  const f = (n: number) => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
  };
  const r = Math.round(f(5) * 255);
  const g = Math.round(f(3) * 255);
  const b = Math.round(f(1) * 255);
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

function hexToHsv(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d + 6) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return [h, max ? d / max : 0, max];
}

function ColorPicker({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const svRef = useRef<HTMLDivElement>(null);
  const [hsv, setHsv] = useState<[number, number, number]>(() => hexToHsv(value));
  const [hexInput, setHexInput] = useState(value);
  const dragging = useRef<'sv' | 'hue' | null>(null);

  useEffect(() => {
    setHsv(hexToHsv(value));
    setHexInput(value);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (
        popRef.current &&
        !popRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const commit = (h: number, s: number, v: number) => {
    setHsv([h, s, v]);
    const hex = hsvToHex(h, s, v);
    setHexInput(hex);
    onChange(hex);
  };

  const onSvMouse = (e: React.MouseEvent | MouseEvent) => {
    const rect = svRef.current?.getBoundingClientRect();
    if (!rect) return;
    const s = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const v = Math.max(0, Math.min(1, 1 - (e.clientY - rect.top) / rect.height));
    commit(hsv[0], s, v);
  };

  const onHueMouse = (e: React.MouseEvent | MouseEvent, el: HTMLDivElement) => {
    const rect = el.getBoundingClientRect();
    const h = Math.max(0, Math.min(360, ((e.clientX - rect.left) / rect.width) * 360));
    commit(h, hsv[1], hsv[2]);
  };

  useEffect(() => {
    if (!open) return;
    const onMove = (e: MouseEvent) => {
      if (dragging.current === 'sv') onSvMouse(e);
      else if (dragging.current === 'hue') {
        const hueEl = popRef.current?.querySelector('[data-hue-bar]') as HTMLDivElement;
        if (hueEl) onHueMouse(e, hueEl);
      }
    };
    const onUp = () => {
      dragging.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  });

  const pos = triggerRef.current?.getBoundingClientRect();

  return (
    <>
      <div
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        style={{
          position: 'relative',
          width: 32,
          height: 32,
          borderRadius: '12px',
          background: 'rgba(var(--tx-ink-rgb), 0.05)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}>
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: 6,
            background: value,
            border: '1px solid var(--tx-line)',
          }}
        />
      </div>
      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            style={{
              position: 'fixed',
              top: pos.bottom + 8,
              left: pos.left - 100,
              zIndex: 9999,
              width: 240,
              borderRadius: '24px',
              padding: 16,
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              boxShadow: 'var(--tx-shadow)',
              fontFamily: font,
            }}>
            {/* SV canvas */}
            <div
              ref={svRef}
              onMouseDown={(e) => {
                dragging.current = 'sv';
                onSvMouse(e);
              }}
              style={{
                position: 'relative',
                width: '100%',
                height: 140,
                borderRadius: '12px',
                cursor: 'crosshair',
                marginBottom: 12,
                background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv[0]}, 100%, 50%))`,
              }}>
              <div
                style={{
                  position: 'absolute',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: '2px solid var(--tx-line)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                  left: `calc(${hsv[1] * 100}% - 7px)`,
                  top: `calc(${(1 - hsv[2]) * 100}% - 7px)`,
                  pointerEvents: 'none',
                }}
              />
            </div>
            {/* Hue bar */}
            <div
              data-hue-bar
              onMouseDown={(e) => {
                dragging.current = 'hue';
                onHueMouse(e, e.currentTarget as HTMLDivElement);
              }}
              style={{
                position: 'relative',
                width: '100%',
                height: 14,
                borderRadius: 7,
                cursor: 'pointer',
                marginBottom: 12,
                background: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)',
              }}>
              <div
                style={{
                  position: 'absolute',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: '2px solid var(--tx-line)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                  left: `calc(${(hsv[0] / 360) * 100}% - 7px)`,
                  top: 0,
                  pointerEvents: 'none',
                  background: `hsl(${hsv[0]}, 100%, 50%)`,
                }}
              />
            </div>
            {/* Hex input */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: value,
                  border: '1px solid var(--tx-line)',
                  flexShrink: 0,
                }}
              />
              <input
                value={hexInput}
                onChange={(e) => {
                  setHexInput(e.target.value);
                  if (/^#[0-9a-fA-F]{6}$/.test(e.target.value)) {
                    setHsv(hexToHsv(e.target.value));
                    onChange(e.target.value);
                  }
                }}
                style={{
                  flex: 1,
                  background: 'var(--tx-fill)',
                  border: 0,
                  borderRadius: 8,
                  padding: '6px 10px',
                  fontSize: 12,
                  color: C.text,
                  fontFamily: 'monospace',
                  outline: 'none',
                }}
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

// ─── SaveBar (sticky bottom) ────────────────────────────────────────────────

function SaveBar({
  dirty,
  version,
  lastSaved,
  onSave,
  onDiscard,
  onRescore,
}: {
  dirty: boolean;
  version: string;
  lastSaved: string | null;
  onSave: () => void;
  onDiscard: () => void;
  onRescore: (scoreType: 'home' | 'area') => Promise<void>;
}) {
  const [rescoreStep, setRescoreStep] = useState<
    'idle' | 'confirm' | 'running' | 'done' | 'failed'
  >('idle');
  const [rescoreInput, setRescoreInput] = useState('');
  const [rescoreType, setRescoreType] = useState<'home' | 'area'>('home');
  const rescoreLabel = rescoreType === 'home' ? 'The Home' : 'The Area';
  const confirmationText = rescoreType === 'home' ? 'Rescore All Homes' : 'Rescore All Areas';
  const status = (color: string, text: string) => (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12,
        fontWeight: 500,
        color: C.text,
        fontFamily: font,
        whiteSpace: 'nowrap',
      }}>
      <Tick color={color} />
      {text}
    </span>
  );
  const deployIcon = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 00-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 012-3.95A12.88 12.88 0 0122 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 01-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 3 0 3 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-3 0-3" />
    </svg>
  );
  const warnIcon = (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
  return (
    // A floating glass sheet that sticks to the scroll area's bottom gutter (the page's own 20px bottom padding, so it
    // lines up with the sidebar's foot on the frame); the margin above keeps it clear of the last card at the end
    <div
      style={{
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        marginTop: 20,
        background: 'var(--tx-glass-panel)',
        backgroundImage: 'var(--tx-surface-light)',
        backdropFilter: 'blur(24px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
        borderRadius: 24,
        padding: '12px 12px 12px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        boxShadow: 'var(--tx-shadow)',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
        {dirty ? (
          status(L.critical, 'Unsaved changes')
        ) : (
          <span style={{ fontSize: 12, fontWeight: 500, color: C.muted, fontFamily: font }}>
            All changes saved {'\u2014'} v{version}
            {lastSaved && (
              <>
                {' \u00B7'} Last updated{' '}
                {new Date(lastSaved).toLocaleDateString('en-US', {
                  month: '2-digit',
                  day: '2-digit',
                  year: 'numeric',
                })}{' '}
                {new Date(lastSaved).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </>
            )}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {dirty && <Button onClick={onDiscard}>Discard</Button>}
        {/* the screen's one lime action, lit only while there is something to deploy */}
        <Button
          variant={dirty ? 'primary' : 'secondary'}
          icon={deployIcon}
          onClick={onSave}
          sx={dirty ? undefined : { color: C.muted, cursor: 'default' }}>
          Deploy
        </Button>
        {rescoreStep === 'idle' &&
          (['home', 'area'] as const).map((scoreType) => (
            <Button
              key={scoreType}
              variant="danger"
              icon={warnIcon}
              onClick={() => {
                setRescoreType(scoreType);
                setRescoreStep('confirm');
              }}>
              Rescore All {scoreType === 'home' ? 'Homes' : 'Areas'}
            </Button>
          ))}
        {rescoreStep === 'confirm' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {status(L.critical, `Type "${confirmationText}" to confirm`)}
            <TextInput
              value={rescoreInput}
              onChange={(e) => setRescoreInput(e.target.value)}
              placeholder={confirmationText}
              inputProps={{ 'aria-label': `Type ${confirmationText} to confirm` }}
              sx={{ width: 200 }}
            />
            <Button
              variant="danger"
              onClick={async () => {
                setRescoreStep('running');
                setRescoreInput('');
                try {
                  await onRescore(rescoreType);
                  setRescoreStep('done');
                  setTimeout(() => setRescoreStep('idle'), 3000);
                } catch {
                  setRescoreStep('failed');
                  setTimeout(() => setRescoreStep('idle'), 4000);
                }
              }}
              disabled={rescoreInput !== confirmationText}>
              Confirm
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setRescoreStep('idle');
                setRescoreInput('');
              }}>
              Cancel
            </Button>
          </div>
        )}
        {rescoreStep === 'running' && status(L.urgent, `Rescoring ${rescoreLabel}\u2026`)}
        {rescoreStep === 'done' && status(L.resolved, `${rescoreLabel} rescore queued`)}
        {rescoreStep === 'failed' && status(L.critical, 'Rescore failed')}
      </div>
    </div>
  );
}

// ─── Page: Score Weights (3-tier hierarchy) ─────────────────────────────────

const URGENCY_COLORS: Record<string, string> = {
  Critical: L.critical,
  Urgent: L.urgent,
  Moderate: L.moderate,
  Low: L.low,
  Monitor: L.monitor,
};

function PageWeights({
  state,
  update,
  contextRegistry,
}: {
  state: CmsState;
  update: (key: string, value: any) => void;
  contextRegistry: ContextRegistry | null;
}) {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const registryCategories = new Map(
    (contextRegistry?.categories || []).map((category) => [category.key, category]),
  );

  const renderSubfactors = (category: ContextRegistryCategory) => (
    <div
      style={{
        width: '100%',
        padding: '0 20px',
        boxSizing: 'border-box',
      }}>
      <div
        style={{ fontSize: 11, fontWeight: 500, color: C.muted, padding: '10px 0 8px', fontFamily: font }}>
        Read-only registry · {category.subfactors.length} subfactors · version{' '}
        {contextRegistry?.version}
      </div>
      {category.subfactors.map((subfactor, index) => (
        <div
          key={subfactor.key}
          style={{
            padding: `12px 0 ${index === category.subfactors.length - 1 ? 0 : 12}px`,
            borderTop: `1px solid ${C.borderLight}`,
          }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <div style={{ fontSize: 11, color: C.text, fontWeight: 600, fontFamily: font }}>
              {subfactor.display_name}
            </div>
            <span style={{ flex: 1 }} />
            <div style={{ fontSize: 11, color: C.muted, fontFamily: font }}>
              {Number(subfactor.weight.toFixed(2))}%
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 2 }}>
            <div style={{ fontSize: 11, color: C.subtle, fontFamily: 'monospace' }}>
              {subfactor.key}
            </div>
            <span style={{ flex: 1 }} />
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                color: subfactor.enabled ? C.text : C.muted,
                fontSize: 11,
                fontWeight: 500,
                fontFamily: font,
              }}>
              <Tick color={subfactor.enabled ? L.resolved : C.subtle} />
              {subfactor.enabled ? 'Enabled' : 'Disabled'}
            </div>
          </div>
          <div
            style={{
              width: '100%',
              marginTop: 10,
              fontSize: 11,
              color: C.subtle,
              fontFamily: 'monospace',
            }}>
            {subfactor.sources.map((source) => (
              <div
                key={source.path}
                style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 3 }}>
                <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                  <span style={{ color: C.muted }}>{source.path}</span>
                  {source.fallback_path && (
                    <span style={{ display: 'block', color: C.subtle, marginTop: 2 }}>
                      fallback: {source.fallback_path}
                    </span>
                  )}
                  {source.distance_bands_miles && (
                    <span style={{ display: 'block', color: C.muted, marginTop: 2 }}>
                      distance bands: {source.distance_bands_miles.join(' / ')} mi
                      {source.coverage_radius_miles != null
                        ? ` · coverage: ${source.target_count ?? 1} within ${
                            source.coverage_radius_miles
                          } mi`
                        : ''}
                    </span>
                  )}
                </span>
                <span style={{ flexShrink: 0, textAlign: 'right', color: C.muted }}>
                  {Number(source.weight.toFixed(2))}%
                </span>
              </div>
            ))}
            <div style={{ color: C.muted, fontFamily: font, marginTop: 3 }}>
              {subfactor.transform.replace(/_/g, ' ')} · higher score is better
            </div>
          </div>
        </div>
      ))}
    </div>
  );
  const buildGroups = (
    subscores: Record<string, SubscoreEntry>,
    categories: Record<string, CategoryEntry>,
    colorMap: Record<string, string>,
  ) => {
    const groups: Record<
      string,
      SubscoreEntry & { color: string; cats: [string, CategoryEntry][] }
    > = {};
    Object.entries(subscores).forEach(([k, v]) => {
      groups[k] = { ...v, color: colorMap[k], cats: [] };
    });
    Object.entries(categories).forEach(([catKey, catVal]) => {
      if (groups[catVal.subscore]) groups[catVal.subscore].cats.push([catKey, catVal]);
    });
    return groups;
  };

  const condGroups = buildGroups(state.condSubscores, state.condCategories, {
    homeHealth: '#bbb7af',
    repairBurden: '#bbb7af',
  });

  const ctxGroups = buildGroups(state.ctxSubscores, state.ctxCategories, {
    locationQuality: '#bbb7af',
    riskExposure: '#bbb7af',
  });

  const renderScoreBlock = ({
    title,
    subscores,
    groups,
    subscoreKey,
    categoryKey,
    accent,
  }: {
    title: string;
    subscores: Record<string, SubscoreEntry>;
    groups: Record<string, SubscoreEntry & { color: string; cats: [string, CategoryEntry][] }>;
    subscoreKey: string;
    categoryKey: string;
    accent: string;
  }) => {
    const subscoreSum = Object.values(subscores).reduce((s, x) => s + x.weight, 0);
    const subscoreOk = Math.abs(subscoreSum - 100) < 0.01;

    // Build globally unique category color map across all subscores
    const catColorMap: Record<string, string> = {};
    const usedColors = new Set<string>();
    // First pass: accent color for each subscore's first category
    Object.values(groups).forEach((group) => {
      if (group.cats.length > 0) {
        catColorMap[group.cats[0][0]] = group.color;
        usedColors.add(group.color);
      }
    });
    // Second pass: assign remaining categories from palette, skipping used colors
    const available = SEGMENT_PALETTE.filter((c) => !usedColors.has(c));
    let availIdx = 0;
    Object.values(groups).forEach((group) => {
      group.cats.slice(1).forEach(([catKey]) => {
        const color = available[availIdx % available.length];
        catColorMap[catKey] = color;
        usedColors.add(color);
        availIdx++;
      });
    });

    return (
      <div>
        {/* L1: Composite formula card */}
        <div
          style={{
            background: C.card,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            borderRadius: '24px',
            padding: 20,
            marginBottom: 20,
          }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 14,
            }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: C.text, fontFamily: font }}>
                {title}
              </span>
              <TierBadge label="Composite" color={C.muted} />
            </div>
            <WeightSum
              weights={Object.fromEntries(Object.entries(subscores).map(([k, v]) => [k, v.weight]))}
            />
          </div>
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: 12,
              color: C.muted,
              padding: '11px 14px',
              background: 'var(--tx-fill)',
              borderRadius: 10,
              lineHeight: 1.8,
              textAlign: 'left',
            }}>
            <span style={{ color: 'var(--tx-muted)', fontWeight: 600 }}>{title}</span>
            <span style={{ color: C.subtle }}> = </span>
            {Object.entries(subscores)
              .sort(
                ([, a], [, b]) =>
                  Number(isHiddenSubscore(a.label)) - Number(isHiddenSubscore(b.label)),
              )
              .map(([k, v], i, arr) => (
                <span key={k}>
                  <span style={{ color: groups[k]?.color || accent }}>({v.label}</span>
                  <span style={{ color: C.subtle }}> {'\u00d7'} </span>
                  <span style={{ color: C.text, fontWeight: 600 }}>
                    {(v.weight / 100).toFixed(2)}
                  </span>
                  <span style={{ color: groups[k]?.color || accent }}>)</span>
                  {i < arr.length - 1 && <span style={{ color: C.subtle }}> + </span>}
                </span>
              ))}
          </div>
        </div>

        {/* L2 + L3: Subscore cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {Object.entries(groups)
            .sort(
              ([, a], [, b]) =>
                Number(isHiddenSubscore(a.label)) - Number(isHiddenSubscore(b.label)),
            )
            .map(([ssKey, group]) => {
              const catSum = group.cats.reduce((s, [, v]) => s + v.weight, 0);
              const catOk = Math.abs(catSum - 100) < 0.01;
              const hasCats = group.cats.length > 0;
              // Hidden subscores stay editable here so admins can see/adjust
              // historic config, but render dimmed and sorted last so it's
              // obvious they don't surface in the launch UI.
              const subscoreIsHidden = isHiddenSubscore(group.label);

              return (
                <div
                  key={ssKey}
                  style={{
                    background: C.card,
                    backgroundImage: 'var(--tx-surface-light)',
                    boxShadow: 'var(--tx-shadow)',
                    borderRadius: '24px',
                    overflow: 'hidden',
                    opacity: subscoreIsHidden ? 0.45 : 1,
                  }}>
                  {/* L2: Subscore row */}
                  <div
                    style={{
                      padding: '14px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: C.text, fontFamily: font }}>
                        {group.label}
                      </span>
                      <TierBadge label="Subscore" color={group.color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }} />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: C.muted,
                        fontFamily: font,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                      }}>
                      "{group.question}"
                    </span>
                    <NumInput
                      value={group.weight}
                      unit="%"
                      warn={!subscoreOk}
                      onChange={(val) =>
                        update(subscoreKey, {
                          ...subscores,
                          [ssKey]: { label: group.label, weight: val, question: group.question },
                        })
                      }
                    />
                  </div>
                  <div style={{ height: 1, margin: '0 20px', background: C.border }} />

                  {/* L3: Category weights */}
                  {hasCats && (
                    <div
                      style={{
                        padding: categoryKey === 'ctxCategories' ? '14px 20px 0' : '14px 20px 16px',
                      }}>
                      <div
                        style={{
                          display: 'flex',
                          gap: 20,
                          alignItems: ssKey === 'homeHealth' ? 'stretch' : 'center',
                        }}>
                        {/* Left half: the donut and the urgency bars, centred in their half so the card reads as
                            two balanced columns rather than a chart in one corner and inputs in the other */}
                        <div
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 28,
                          }}>
                          <div style={{ marginTop: ssKey === 'homeHealth' ? 10 : 0 }}>
                            <CategoryDonut
                              cats={group.cats}
                              colors={group.cats.map(([k]) => catColorMap[k])}
                              scale={categoryKey === 'ctxCategories' ? 0.65 : 1}
                            />
                          </div>
                          {ssKey === 'homeHealth' && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                              }}>
                              <div style={{ width: 200 }}>
                                <Bars
                                  height={64}
                                  gap={12}
                                  format={(v) => `${v} pts`}
                                  data={['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'].map((u) => ({
                                    label: u,
                                    value: state.urgencyPoints[u],
                                    color: URGENCY_COLORS[u],
                                  }))}
                                />
                                <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                                  {['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'].map((u) => (
                                    <span
                                      key={u}
                                      style={{
                                        flex: 1,
                                        textAlign: 'center',
                                        fontSize: 10,
                                        fontWeight: 500,
                                        color: C.subtle,
                                        fontFamily: font,
                                      }}>
                                      {u.slice(0, 3)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                        {/* Right half: categories + urgency inputs, each name left and its value right */}
                        <div
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                          }}>
                          {group.cats.map(([catKey, cat], catIdx) => (
                            <div key={catKey} style={{ width: '100%' }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 12,
                                  marginBottom: catIdx === group.cats.length - 1 ? 0 : 8,
                                }}>
                                <Tick color={catColorMap[catKey]} />
                                <span
                                  style={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: C.text,
                                    fontFamily: font,
                                  }}>
                                  {sentenceCase(catKey)}
                                </span>
                                <NumInput
                                  value={cat.weight}
                                  unit="%"
                                  warn={!catOk}
                                  onChange={(val) =>
                                    update(categoryKey, {
                                      ...(categoryKey === 'condCategories'
                                        ? state.condCategories
                                        : state.ctxCategories),
                                      [catKey]: { ...cat, weight: val },
                                    })
                                  }
                                />
                              </div>
                            </div>
                          ))}

                          {/* Urgency Points — under HomeHealth */}
                          {ssKey === 'homeHealth' && (
                            <div
                              style={{
                                marginTop: 16,
                                paddingTop: 14,
                                borderTop: `1px solid ${C.border}`,
                              }}>
                              <Label>Urgency points (P_i)</Label>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: C.subtle,
                                  fontStyle: 'italic',
                                  fontFamily: font,
                                  marginBottom: 10,
                                }}>
                                S_c_raw = max(0, 100 {'\u2212'} {'\u03a3'}(P_i {'\u00d7'} M_i))
                              </div>
                              {['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'].map((u) => (
                                <div
                                  key={u}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    marginBottom: 7,
                                  }}>
                                  <Tick color={URGENCY_COLORS[u]} />
                                  <span
                                    style={{
                                      flex: 1,
                                      minWidth: 0,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: C.text,
                                      flexShrink: 0,
                                      fontFamily: font,
                                    }}>
                                    {u}
                                  </span>
                                  <NumInput
                                    value={state.urgencyPoints[u]}
                                    min={0}
                                    max={20}
                                    step={1}
                                    unit="pts"
                                    onChange={(val) =>
                                      update('urgencyPoints', { ...state.urgencyPoints, [u]: val })
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Context registry — full-width pillar accordions */}
                      {categoryKey === 'ctxCategories' && (
                        <div
                          style={{
                            width: 'calc(100% + 40px)',
                            margin: '14px -20px 0',
                          }}>
                          <div style={{ height: 1, margin: '0 20px', background: C.border }} />
                          {group.cats.map(([catKey], index) => {
                            const registryCategory = registryCategories.get(catKey);
                            if (!registryCategory) return null;

                            const isExpanded = expandedCategory === catKey;
                            return (
                              <div
                                key={catKey}
                                style={{
                                  paddingBottom:
                                    index === group.cats.length - 1 ? (isExpanded ? 20 : 5) : 0,
                                }}>
                                <button
                                  type="button"
                                  aria-expanded={isExpanded}
                                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${
                                    registryCategory.name
                                  } subfactors`}
                                  onClick={() => setExpandedCategory(isExpanded ? null : catKey)}
                                  style={{
                                    width: '100%',
                                    height: 58,
                                    padding: '0 20px',
                                    border: 'none',
                                    background: 'transparent',
                                    color: C.text,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 14,
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    fontFamily: font,
                                  }}>
                                  <div style={{ minWidth: 0 }}>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                      }}>
                                      {registryCategory.name}
                                    </div>
                                    <div style={{ marginTop: 3, fontSize: 11, color: C.muted }}>
                                      {registryCategory.subfactors.length} subfactors
                                    </div>
                                  </div>
                                  <span style={{ flex: 1 }} />
                                  <span
                                    style={{
                                      display: 'inline-flex',
                                      color: C.muted,
                                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.2s',
                                    }}>
                                    <ChevronDown size={16} />
                                  </span>
                                </button>
                                {isExpanded && renderSubfactors(registryCategory)}
                                {index < group.cats.length - 1 && (
                                  <div
                                    style={{
                                      height: 1,
                                      margin: '0 20px',
                                      background: C.borderLight,
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* BigTicket + Normalization — full width along card bottom */}
                      {ssKey === 'homeHealth' && (
                        <div
                          style={{
                            marginTop: 20,
                            paddingTop: 20,
                            borderTop: `1px solid ${C.border}`,
                          }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <div>
                              <Label>BigTicket multiplier (M_i)</Label>
                              <p
                                style={{
                                  margin: '0 0 4px',
                                  fontSize: 11,
                                  color: C.subtle,
                                  fontFamily: font,
                                }}>
                                Multiplier
                              </p>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <NumInput
                                  value={state.bigTicketMultiplier}
                                  min={1.0}
                                  max={3.0}
                                  step={0.1}
                                  unit={'\u00d7'}
                                  onChange={(val) => update('bigTicketMultiplier', val)}
                                />
                                <span style={{ fontSize: 11, color: C.subtle, fontFamily: font }}>
                                  53 / 121 components flagged
                                </span>
                              </div>
                              <p
                                style={{
                                  margin: '11px 0 0',
                                  fontSize: 11,
                                  color: C.subtle,
                                  fontFamily: font,
                                }}>
                                Not applied to RepairBurden
                              </p>
                            </div>
                            <div>
                              <Label>Normalization (category level)</Label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div>
                                  <p
                                    style={{
                                      margin: '0 0 4px',
                                      fontSize: 11,
                                      color: C.subtle,
                                      fontFamily: font,
                                    }}>
                                    Floor
                                  </p>
                                  <NumInput
                                    value={state.normalization.floor}
                                    min={0}
                                    max={20}
                                    step={1}
                                    onChange={(val) =>
                                      update('normalization', {
                                        ...state.normalization,
                                        floor: val,
                                      })
                                    }
                                  />
                                </div>
                                <div>
                                  <p
                                    style={{
                                      margin: '0 0 4px',
                                      fontSize: 11,
                                      color: C.subtle,
                                      fontFamily: font,
                                    }}>
                                    Ceiling
                                  </p>
                                  <NumInput
                                    value={state.normalization.ceiling}
                                    min={80}
                                    max={100}
                                    step={1}
                                    onChange={(val) =>
                                      update('normalization', {
                                        ...state.normalization,
                                        ceiling: val,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                              <div
                                style={{
                                  marginTop: 6,
                                  fontFamily: 'monospace',
                                  fontSize: 11,
                                  color: C.muted,
                                  padding: '5px 9px',
                                  background: 'rgba(var(--tx-ink-rgb), 0.05)',
                                  borderRadius: '9px',
                                  textAlign: 'left',
                                }}>
                                {state.normalization.floor} + (raw {'\u00d7'}{' '}
                                {(
                                  (state.normalization.ceiling - state.normalization.floor) /
                                  100
                                ).toFixed(2)}
                                )
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* L3: Formula parameters (RepairBurden — no category children) */}
                  {!hasCats && (
                    <div style={{ padding: '14px 20px 16px' }}>
                      <TierBadge label="Formula parameters" color={C.subtle} />

                      {/* RepairBurden */}
                      {ssKey === 'repairBurden' && (
                        <div style={{ marginTop: 14 }}>
                          <div style={{ display: 'flex', gap: 20, alignItems: 'stretch' }}>
                            {/* Left column — match HomeHealth left column width */}
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                flexShrink: 0,
                                width: 230,
                              }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-end',
                                  gap: 12,
                                  marginTop: 'auto',
                                  justifyContent: 'center',
                                }}>
                                {['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'].map((u) => {
                                  const h = (state.urgencyCostMult[u] / 3.0) * 150;
                                  return (
                                    <div
                                      key={u}
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 4,
                                      }}>
                                      <div
                                        style={{
                                          width: 24,
                                          height: h,
                                          minHeight: 3,
                                          background: URGENCY_COLORS[u],
                                          borderRadius: 3,
                                        }}
                                      />
                                      <span
                                        style={{
                                          fontSize: 11,
                                          color: C.subtle,
                                          fontFamily: font,
                                        }}>
                                        {u.slice(0, 3)}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            {/* Right column: label + inputs */}
                            <div
                              style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                              }}>
                              <Label>Urgency cost multipliers (U_i)</Label>
                              {['Critical', 'Urgent', 'Moderate', 'Low', 'Monitor'].map((u) => (
                                <div
                                  key={u}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    marginBottom: 7,
                                  }}>
                                  <Tick color={URGENCY_COLORS[u]} />
                                  <span
                                    style={{
                                      width: 140,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      color: C.text,
                                      flexShrink: 0,
                                      fontFamily: font,
                                    }}>
                                    {u}
                                  </span>
                                  <NumInput
                                    value={state.urgencyCostMult[u]}
                                    min={0}
                                    max={10}
                                    step={0.1}
                                    unit={'\u00d7'}
                                    onChange={(val) =>
                                      update('urgencyCostMult', {
                                        ...state.urgencyCostMult,
                                        [u]: val,
                                      })
                                    }
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                          <div
                            style={{
                              marginTop: 20,
                              paddingTop: 20,
                              borderTop: `1px solid ${C.border}`,
                            }}>
                            <Label>Decay curve parameter {'\u03ba'}</Label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                              <NumInput
                                value={state.repairBurden.curveParam}
                                min={1}
                                max={50}
                                step={1}
                                onChange={(val) =>
                                  update('repairBurden', { ...state.repairBurden, curveParam: val })
                                }
                              />
                              <div
                                style={{
                                  flex: 1,
                                  fontFamily: 'monospace',
                                  fontSize: 12,
                                  color: C.muted,
                                  padding: '9px 12px',
                                  background: 'rgba(var(--tx-ink-rgb), 0.05)',
                                  borderRadius: '12px',
                                  textAlign: 'left',
                                }}>
                                B_raw = 100 / (1 + ({'\u03b2'} / {state.repairBurden.curveParam}))
                              </div>
                            </div>
                            <p
                              style={{
                                margin: '7px 0 0',
                                fontSize: 11,
                                color: C.subtle,
                                fontFamily: font,
                              }}>
                              {state.repairBurden.note}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ minWidth: 0 }}>
          {renderScoreBlock({
            title: 'ConditionScore',
            subscores: state.condSubscores,
            groups: condGroups,
            subscoreKey: 'condSubscores',
            categoryKey: 'condCategories',
            accent: C.lime,
          })}
        </div>
        <div style={{ minWidth: 0 }}>
          {renderScoreBlock({
            title: 'ContextScore',
            subscores: state.ctxSubscores,
            groups: ctxGroups,
            subscoreKey: 'ctxSubscores',
            categoryKey: 'ctxCategories',
            accent: L.low,
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Page: Grading (Bands + Transforms) ─────────────────────────────────────

function PageGrading({
  state,
  update,
}: {
  state: CmsState;
  update: (key: string, value: any) => void;
}) {
  const grades = ['A', 'B', 'C', 'D', 'F'];
  const bands = state.gradeBands;

  const validateBands = () => {
    for (let i = 0; i < grades.length - 1; i++) {
      if (bands[grades[i]].min <= bands[grades[i + 1]].min) return false;
    }
    return true;
  };
  const valid = validateBands();

  return (
    <div>
      {/* ─── 2×2 grid: Bands + Belt (top), Transforms (bottom) ─── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
          gap: 20,
        }}>
        {/* Top-left: Grade Bands */}
        <div
          style={{
            background: C.card,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            borderRadius: '24px',
            padding: 20,
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: C.text, fontFamily: font }}>
              Grade bands
            </span>
            <TierBadge label="A–F thresholds" color={C.muted} />
          </div>

          {/* Visual band bar */}
          <Label>Score band visualization (5–95 effective range)</Label>
          {(() => {
            const parts = grades
              .map((g, i) => {
                const high = i === 0 ? 95 : bands[grades[i - 1]].min - 1;
                return { label: g, value: high - bands[g].min + 1, color: bands[g].color };
              })
              .filter((p) => p.value > 0);
            return (
              <div style={{ marginBottom: 20 }}>
                <ShareStrip parts={parts} format={(v) => `${v} pts`} />
                <div style={{ display: 'flex', gap: 2, marginTop: 6 }}>
                  {parts.map((p) => (
                    <span
                      key={p.label}
                      style={{
                        flex: p.value,
                        minWidth: 0,
                        textAlign: 'center',
                        fontSize: 10,
                        fontWeight: 500,
                        color: C.subtle,
                        fontFamily: font,
                      }}>
                      {p.label}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Column headers */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 6,
            }}>
            <div style={{ width: 40 }} />
            <div style={{ flex: 1, textAlign: 'left' }}>
              <Label>Label</Label>
            </div>
            <div style={{ width: 60, textAlign: 'center' }}>
              <Label>Range</Label>
            </div>
            <div style={{ width: 80, textAlign: 'center' }}>
              <Label>Min</Label>
            </div>
            <div style={{ width: 36, textAlign: 'center' }}>
              <Label>Color</Label>
            </div>
          </div>

          {/* Band rows: separated by hairlines, not boxed */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {grades.map((g, i) => {
              const band = bands[g];
              const prevGrade = i > 0 ? grades[i - 1] : null;
              const nextGrade = i < grades.length - 1 ? grades[i + 1] : null;
              const upperBound = prevGrade ? bands[prevGrade].min - 1 : 95;
              const isFrozen = g === 'F';
              return (
                <div
                  key={g}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 0',
                    borderTop: i > 0 ? `1px solid ${C.border}` : 'none',
                  }}>
                  <div
                    style={{
                      width: 40,
                      height: 28,
                      borderRadius: 8,
                      background: band.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: 14,
                      color: 'var(--tx-on-lime)',
                      flexShrink: 0,
                      fontFamily: font,
                    }}>
                    {g}
                  </div>
                  <div
                    style={{
                      flex: 1,
                      fontSize: 12,
                      color: C.muted,
                      fontFamily: font,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                    {band.label}
                  </div>
                  <div
                    style={{
                      width: 60,
                      fontSize: 12,
                      color: C.text,
                      fontWeight: 600,
                      fontFamily: font,
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                    }}>
                    {isFrozen ? '5' : band.min}–{upperBound}
                  </div>
                  <div
                    style={{ width: 80, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {isFrozen ? (
                      <span
                        style={{
                          fontSize: 11,
                          color: C.subtle,
                          fontStyle: 'italic',
                          fontFamily: font,
                        }}>
                        Floor
                      </span>
                    ) : (
                      <NumInput
                        value={band.min}
                        min={nextGrade ? bands[nextGrade].min + 1 : 1}
                        max={upperBound}
                        warn={!valid}
                        onChange={(val) =>
                          update('gradeBands', { ...bands, [g]: { ...band, min: val } })
                        }
                      />
                    )}
                  </div>
                  <div
                    style={{ width: 36, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    <ColorPicker
                      value={band.color}
                      onChange={(hex) =>
                        update('gradeBands', { ...bands, [g]: { ...band, color: hex } })
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {!valid && (
            <div
              style={{
                marginTop: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                fontWeight: 500,
                color: C.text,
                fontFamily: font,
              }}>
              <Tick color={L.urgent} />
              Thresholds must be strictly descending.
            </div>
          )}
        </div>

        {/* Top-right: Academic Belt Scale */}
        <div
          style={{
            background: C.card,
            backgroundImage: 'var(--tx-surface-light)',
            boxShadow: 'var(--tx-shadow)',
            borderRadius: '24px',
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: C.text, fontFamily: font }}>
              Academic belt scale
            </span>
            <TierBadge label="Reference" color={C.muted} />
          </div>
          <p style={{ margin: '0 0 20px', fontSize: 12, color: C.muted, fontFamily: font }}>
            Both tables should conform to the 93/83/73/63/53 academic belt pattern with 10-point
            gaps.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {[93, 83, 73, 63, 53].map((v, i) => {
              const g = grades[i];
              return (
                <div
                  key={v}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 0',
                    borderTop: i > 0 ? `1px solid ${C.border}` : 'none',
                  }}>
                  <div
                    style={{
                      width: 40,
                      height: 28,
                      borderRadius: 8,
                      background: bands[g].color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'var(--tx-on-lime)',
                      flexShrink: 0,
                      fontFamily: font,
                    }}>
                    {g}
                  </div>
                  <span style={{ flex: 1, fontSize: 12, color: C.muted, fontFamily: font }}>
                    {bands[g].label}
                  </span>
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 300,
                      letterSpacing: '-0.03em',
                      color: C.text,
                      fontVariantNumeric: 'tabular-nums',
                      fontFamily: font,
                    }}>
                    {v}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Page: LLM Content ──────────────────────────────────────────────────────

function BlurbControls({ text }: { text: string }) {
  const chars = text.length;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const lines = text.trim() ? text.split(/\n/).length : 0;

  const metrics = [
    { label: 'Chars', value: chars },
    { label: 'Words', value: words },
    { label: 'Lines', value: lines },
  ];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 8 }}>
      {metrics.map((m) => (
        <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: C.muted,
              fontFamily: font,
            }}>
            {m.label}
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: C.text,
              fontVariantNumeric: 'tabular-nums',
              fontFamily: font,
            }}>
            {m.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function PageContent({
  state,
  update,
}: {
  state: CmsState;
  update: (key: string, value: any) => void;
}) {
  type PromptKey =
    | 'askHauserPrompt'
    | 'documentClassificationPrompt'
    | 'issueExtractionPrompt'
    | 'conditionExplainerPrompt'
    | 'contextExplainerPrompt';

  const promptTabs: Array<{
    key: PromptKey;
    label: string;
    title: string;
    badge: string;
    sub: string;
    referenceLabel?: string;
    referenceValues?: string[];
    referenceTone?: 'urgency' | 'plain';
  }> = [
    {
      key: 'askHauserPrompt',
      label: 'Ask Hauser',
      title: 'Ask Hauser — system prompt',
      badge: 'Customer-facing',
      sub: 'Applied at the start of every Ask Hauser conversation. HOA evidence context is injected separately at runtime.',
    },
    {
      key: 'documentClassificationPrompt',
      label: 'Classification',
      title: 'Document classification prompt',
      badge: 'AI pipeline',
      sub: 'Classifies uploaded documents from Pulse OCR. The runtime HOA and seller-disclosure taxonomies are appended separately.',
      referenceLabel: 'Valid document type codes',
      referenceValues: [
        'GHI',
        'POOL',
        'CHIMNEY',
        'HVAC',
        'ELEC',
        'PLUMB',
        'FOUND',
        'ENV',
        'SHI',
        'PEST',
        'ROOF',
        'SEWER',
        'ESTIMATE',
        'INVOICE',
        'NHD',
        'SELLER_DISCLOSURE',
        'HOA_DEMAND',
        'HOA_BOARD_MINUTES',
        'HOA_OPERATING_RULES',
        'HOA_ANNUAL_BUDGET',
        'HOA_INSURANCE_SUMMARY',
        'HOA_CCRS',
        'HOA_BYLAWS',
        'HOA_ARTICLES_OF_INCORPORATION',
        'HOA_FINANCIAL_STATEMENT',
        'HOA_ENVIRONMENTAL_REPORT',
        'HOA_AGE_RESTRICTION',
        'HOA_RENTAL_RESTRICTION',
        'HOA_ASSESSMENT_CHANGE_NOTICE',
        'HOA_TRANSFER_DISCLOSURE_FORM',
        'HOA_CLOSING_INFORMATION',
        'HOA_COMMON_AREA_DEFECT_LIST',
        'HOA_COMMON_AREA_DEFECT_SETTLEMENT',
        'HOA_EEE_INSPECTION_REPORT',
        'HOA_VIOLATION_NOTICE',
        'HOA_PACKAGE',
        'OTHER',
      ],
    },
    {
      key: 'issueExtractionPrompt',
      label: 'Inspection extraction',
      title: 'Issue extraction prompt',
      badge: 'blueprint-mono runtime',
      sub: 'Used by the inspection-report extraction dispatcher. It is separate from the HOA/Pulse document-family path.',
      referenceLabel: 'Issue categories and urgency levels',
      referenceValues: [
        'STRUCTURAL',
        'EXTERIOR',
        'INTERIOR',
        'ELECTRICAL',
        'PLUMBING',
        'HVAC SYSTEMS',
        'Critical',
        'Urgent',
        'Moderate',
        'Low',
        'Monitor',
      ],
      referenceTone: 'urgency',
    },
    {
      key: 'conditionExplainerPrompt',
      label: 'The Home',
      title: 'The Home explainer',
      badge: 'hauser-eq runtime',
      sub: 'Natural-language explanation of The Home score. This configuration is edited here; the explainer runs in hauser-eq.',
      referenceLabel: 'JSON output fields',
      referenceValues: [
        'overall_explanation',
        'subscore_explanations',
        'category_explanations',
        'score_drivers',
      ],
    },
    {
      key: 'contextExplainerPrompt',
      label: 'The Area',
      title: 'The Area explainer',
      badge: 'hauser-eq runtime',
      sub: 'Natural-language explanation of The Area score. This configuration is edited here; the explainer runs in hauser-eq.',
      referenceLabel: 'JSON output fields',
      referenceValues: [
        'overall_explanation',
        'subscore_explanations',
        'category_explanations',
        'subfactor_explanations',
        'score_drivers',
      ],
    },
  ];
  const [activePromptKey, setActivePromptKey] = useState<PromptKey>('askHauserPrompt');
  const activePrompt = promptTabs.find((tab) => tab.key === activePromptKey) ?? promptTabs[0];
  const promptText = state.content[activePrompt.key] || '';

  const urgencyColors: Record<string, string> = {
    Critical: L.critical,
    Urgent: L.urgent,
    Moderate: L.moderate,
    Low: L.low,
    Monitor: L.monitor,
  };

  return (
    <div
      style={{
        height: 'calc(100dvh - 190px)',
        minHeight: 460,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
      {/* Prompt picker: the ds Segmented (compact), scrolling sideways if the pane is narrow */}
      <div style={{ flexShrink: 0, overflowX: 'auto', scrollbarWidth: 'none', marginBottom: 12 }}>
        <Segmented
          size="sm"
          label="Prompt"
          options={promptTabs.map((tab) => [tab.key, tab.label] as [PromptKey, string])}
          value={activePrompt.key}
          onChange={setActivePromptKey}
        />
      </div>

      <div
        style={{
          background: C.card,
          backgroundImage: 'var(--tx-surface-light)',
          boxShadow: 'var(--tx-shadow)',
          borderRadius: '24px',
          border: 'none',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          padding: 20,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: C.text, fontFamily: font }}>
            {activePrompt.title}
          </span>
          <TierBadge label={activePrompt.badge} color={C.muted} />
        </div>
        <p style={{ margin: '0 0 12px', fontSize: 11, color: C.muted, fontFamily: font }}>
          {activePrompt.sub}
        </p>

        <div style={{ flex: 1, minHeight: 0 }}>
          <Textarea
            value={promptText}
            mono
            autoSize={false}
            onChange={(val) => update('content', { ...state.content, [activePrompt.key]: val })}
          />
        </div>

        <BlurbControls text={promptText} />

        {activePrompt.referenceValues && activePrompt.referenceLabel && (
          <details style={{ marginTop: 12, flexShrink: 0 }}>
            <summary
              style={{
                color: C.muted,
                cursor: 'pointer',
                fontFamily: font,
                fontSize: 11,
                fontWeight: 500,
              }}>
              {activePrompt.referenceLabel} ({activePrompt.referenceValues.length})
            </summary>
            <div
              style={{
                marginTop: 10,
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                maxHeight: 104,
                overflowY: 'auto',
                paddingRight: 4,
              }}>
              {activePrompt.referenceValues.map((value) => (
                <span
                  key={value}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 11,
                    background: 'var(--tx-fill)',
                    border: 0,
                    borderRadius: 6,
                    padding: '3px 8px',
                    color: C.text,
                    fontFamily: 'monospace',
                  }}>
                  {activePrompt.referenceTone === 'urgency' && urgencyColors[value] && (
                    <Tick color={urgencyColors[value]} />
                  )}
                  {value}
                </span>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

// ─── Page: Audit Log ────────────────────────────────────────────────────────

function PageAuditLog({ state }: { state: CmsState }) {
  return (
    <div>
      <SectionHead
        title="Audit log"
        sub="All changes to scoring parameters and content. Immutable record."
      />
      <Card>
        {state.auditLog.length === 0 ? (
          <p
            style={{
              margin: 0,
              color: C.muted,
              fontSize: 12,
              fontWeight: 500,
              textAlign: 'center',
              padding: '24px 0',
              fontFamily: font,
            }}>
            No changes logged yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {[...state.auditLog].reverse().map((entry, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 16,
                  padding: '14px 0',
                  borderBottom: i < state.auditLog.length - 1 ? `1px solid ${C.border}` : 'none',
                }}>
                <span style={{ marginTop: 2, display: 'flex' }}>
                  <Tick color={L.resolved} />
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
                    <span
                      style={{ fontSize: 12, fontWeight: 600, color: C.text, fontFamily: font }}>
                      {entry.user}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        background: C.input,
                        borderRadius: 6,
                        padding: '1px 8px',
                        color: C.muted,
                        fontFamily: font,
                      }}>
                      {entry.scope}
                    </span>
                  </div>
                  <p style={{ margin: 0, fontSize: 12, color: C.muted, fontFamily: 'monospace' }}>
                    {entry.field}:{' '}
                    <span style={{ color: C.muted, textDecoration: 'line-through' }}>
                      {String(entry.old)}
                    </span>
                    {' \u2192 '}
                    <span style={{ color: C.text, fontWeight: 600 }}>{String(entry.new)}</span>
                  </p>
                </div>
                <span
                  style={{
                    fontSize: 11,
                    color: C.subtle,
                    whiteSpace: 'nowrap' as const,
                    fontFamily: font,
                  }}>
                  {entry.ts}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Main export ────────────────────────────────────────────────────────────

const NAV_KEYS = NAV.map((n) => n.key);

export const LogicTab = () => {
  const { user } = useAuthContext();
  const { tab: urlTab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();

  const initialTab = urlTab && NAV_KEYS.includes(urlTab) ? urlTab : 'weights';

  const [state, setState] = useState<CmsState>(INITIAL);
  const [savedState, setSavedState] = useState<CmsState>(INITIAL);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextRegistry, setContextRegistry] = useState<ContextRegistry | null>(null);

  // Sync tab from URL on param change
  useEffect(() => {
    if (urlTab && NAV_KEYS.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [urlTab]);

  // Update URL when tab changes
  const handleTabChange = useCallback(
    (key: string) => {
      setActiveTab(key);
      navigate(`/admin/logic/${key}`, { replace: true });
    },
    [navigate],
  );

  // Load config from API on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { getScoringConfig, getScoringConfigAuditLog } = await import('@/services/api/admin');
        const [configRes, auditRes] = await Promise.all([
          getScoringConfig(),
          getScoringConfigAuditLog(),
        ]);

        if (cancelled) return;
        setContextRegistry(configRes.contextRegistry);

        if (configRes.config) {
          const savedConfig = sanitizeDeprecatedConfig(configRes.config);
          const loaded: CmsState = {
            ...INITIAL,
            ...savedConfig,
            content: {
              ...INITIAL.content,
              ...savedConfig?.content,
            },
            version: String(configRes.version),
            lastSaved: configRes.updatedAt || null,
            dirty: false,
            auditLog: auditRes.auditLog || [],
          };
          setState(loaded);
          setSavedState(loaded);
        } else {
          // No config saved yet — use INITIAL defaults
          const withAudit = { ...INITIAL, auditLog: auditRes.auditLog || [] };
          setState(withAudit);
          setSavedState(withAudit);
        }
      } catch (err) {
        console.error('Failed to load scoring config:', err);
        if (!cancelled) setError('Failed to load scoring configuration');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const update = useCallback((key: string, value: any) => {
    setState((prev) => ({ ...prev, [key]: value, dirty: true }));
    setDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    setError(null);

    const missingPrompts = Object.entries(REQUIRED_PROMPT_FIELDS)
      .filter(([key]) => !state.content[key as keyof typeof REQUIRED_PROMPT_FIELDS]?.trim())
      .map(([, label]) => label);
    if (missingPrompts.length > 0) {
      setError(`Required LLM content is missing: ${missingPrompts.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      const { putScoringConfig, getScoringConfigAuditLog } = await import('@/services/api/admin');

      // Build the config payload (exclude UI-only fields)
      const { version: _v, lastSaved: _ls, dirty: _d, auditLog: _al, ...configPayload } = state;

      const result = await putScoringConfig({
        config: configPayload,
        userEmail: user?.email,
      });

      // Refresh audit log after save
      const auditRes = await getScoringConfigAuditLog();

      const updated: CmsState = {
        ...state,
        version: String(result.version),
        lastSaved: result.updatedAt,
        dirty: false,
        auditLog: auditRes.auditLog || [],
      };
      setState(updated);
      setSavedState(updated);
      setDirty(false);
    } catch (err) {
      console.error('Failed to save scoring config:', err);
      setError('Failed to deploy scoring configuration');
    } finally {
      setSaving(false);
    }
  }, [state]);

  const handleDiscard = useCallback(() => {
    setState({ ...savedState });
    setDirty(false);
  }, [savedState]);

  const renderPage = () => {
    switch (activeTab) {
      case 'weights':
        return <PageWeights state={state} update={update} contextRegistry={contextRegistry} />;
      case 'grading':
        return <PageGrading state={state} update={update} />;
      case 'content':
        return <PageContent state={state} update={update} />;
      case 'audit':
        return <PageAuditLog state={state} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div
        style={{
          fontFamily: font,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: C.muted,
          fontSize: 14,
        }}>
        Loading scoring configuration...
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily: font,
        height: '100%',
        overflow: 'auto',
        padding: '20px 0',
        scrollbarWidth: 'none',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {error && (
        <div
          style={{
            background: `${L.critical}22`,
            border: 0,
            color: L.critical,
            padding: '10px 16px',
            borderRadius: 8,
            fontSize: 12,
            fontFamily: font,
            marginBottom: 16,
          }}>
          {error}
        </div>
      )}

      {/* Tab strip: the ds Segmented, with the config version and sync state on its right */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 20,
        }}>
        <Segmented
          label="Logic sections"
          options={NAV.map((item) => [item.key, item.label] as [string, string])}
          value={activeTab}
          onChange={handleTabChange}
        />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, fontWeight: 500, color: C.muted, fontFamily: font }}>
          v{state.version}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            fontWeight: 500,
            color: C.text,
            fontFamily: font,
            marginRight: 4,
          }}>
          <Tick color={saving ? L.urgent : dirty ? L.moderate : L.resolved} />
          {saving ? 'Deploying...' : dirty ? 'Modified' : 'In sync'}
        </span>
      </div>

      {/* Active page content */}
      <div style={{ flex: 1 }}>{renderPage()}</div>

      {/* Sticky save bar */}
      <SaveBar
        dirty={dirty}
        version={state.version}
        lastSaved={state.lastSaved}
        onSave={handleSave}
        onDiscard={handleDiscard}
        onRescore={async (scoreType) => {
          const { rescoreAllProperties } = await import('@/services/api/admin');
          await rescoreAllProperties(scoreType);
        }}
      />
    </div>
  );
};
